/* Неактивная кнопка действия (APP-SPEC §1.14, BotCore.offBtn): стоит на месте, нажатие — дрожь, виброотклик и на 1,6 с вместо подписи
   «чего не хватает» (накладка .bc-offt поверх подписи). Разметка как у прототипа: class="btn off", aria-disabled, data-bc-off.
   Накладка вставляется кадром раньше класса say (иначе переход проявления не идёт), уходит через 260 мс после снятия say.
   В заморозке .still подсказка стоит, пока кнопка не оживёт (кадр её застаёт). */
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type MouseEvent } from 'react';
import { isStill, reduced, shake } from './motion';
import { platform } from '../platform';

export const OFF_MS = 1600;

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Неактивна: чего не хватает; пусто — кнопка активна */
  miss?: string;
  label: string;
}

/** 0 — подписи «чего не хватает» нет, 1 — накладка стоит (гаснет или вот-вот проявится), 2 — накладка видна (say) */
type Phase = 0 | 1 | 2;

export function OffButton({ miss, label, className, onClick, onAnimationEnd, ...rest }: Props) {
  const [entering, setEntering] = useState(() => !!className?.split(/\s+/).includes('fin'));
  useEffect(() => {
    if (!entering) return;
    const timer = window.setTimeout(() => setEntering(false), 900);
    return () => clearTimeout(timer);
  }, [entering]);
  const stableClass = className
    ?.split(/\s+/)
    .filter(name => name !== 'fin')
    .join(' ');
  const [phase, setPhase] = useState<Phase>(0);
  const timers = useRef<number[]>([]);
  const ref = useRef<HTMLButtonElement>(null);
  const off = !!miss;
  const clear = () => {
    timers.current.forEach(t => clearTimeout(t));
    timers.current = [];
  };
  useEffect(() => clear, []);
  /* кнопка ожила — подсказка снимается сразу (сброс при смене пропа, без эффекта) */
  const [wasOff, setWasOff] = useState(off);
  if (wasOff !== off) {
    setWasOff(off);
    if (!off) setPhase(0);
  }
  useEffect(() => {
    if (!off) clear();
  }, [off]);
  const quiet = () => {
    setPhase(1);
    timers.current.push(window.setTimeout(() => setPhase(0), reduced() ? 0 : 260));
  };
  const click = (e: MouseEvent<HTMLButtonElement>) => {
    if (!off) return onClick?.(e);
    /* обработчик прототипа на документе ловит [data-bc-off] тоже: здесь нажатие обработано целиком */
    e.preventDefault();
    e.stopPropagation();
    shake(ref.current);
    platform.haptic('medium');
    clear();
    setPhase(p => (p === 2 ? 2 : 1));
    requestAnimationFrame(() => setPhase(2));
    if (!isStill()) timers.current.push(window.setTimeout(quiet, OFF_MS));
  };
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={
        'btn' + (off ? ' off' : '') + (off && phase === 2 ? ' say' : '') + (entering ? ' fin' : '') + (stableClass ? ' ' + stableClass : '')
      }
      onAnimationEnd={event => {
        if (event.target === event.currentTarget && event.animationName === 'wz-in') setEntering(false);
        onAnimationEnd?.(event);
      }}
      aria-disabled={off ? 'true' : undefined}
      data-bc-off={off ? miss : undefined}
      onClick={click}
    >
      {label}
      {off && phase > 0 && (
        <span className="bc-offt" role="status">
          <span>{miss}</span>
        </span>
      )}
    </button>
  );
}
